/**
 * LLM parse step for variant restrictions. Sends a plain-English description plus
 * the category context; receives structured rules mapped onto the backend's
 * variant-restriction model (see owa-backend variant_restriction_group / _rule).
 *
 * The model targets the backend's four-value condition and its bay/shelf rule
 * grammar directly, so a parsed rule maps one-to-one onto what
 * {@link RoutePostMerchflowSaveVariantRestrictions} persists.
 */
export const RoutePostMerchflowParseVariantRestrictions = "/merchflow/parse-variant-restrictions";

export interface PayloadPostMerchflowParseVariantRestrictions {
  category_id: string;
  description: string;
  /**
   * Names of groups already on screen (e.g. ["Group 1", "Group 2"]). Lets the
   * description target an existing group ("add COOKIES to group 1") instead of
   * always creating a new one.
   */
  existing_group_names?: string[];
}

/**
 * Backend `variant_restriction_rule_condition`. Richer than the pog's three
 * types: `MUST_HAVE_ONLY` means "only these variants, nothing else" and
 * `CAN_HAVE` maps to the pog's `MAY_HAVE`.
 */
export type VariantRestrictionCondition =
  "MUST_HAVE" | "CAN_HAVE" | "MUST_HAVE_ONLY" | "MUST_NOT_HAVE";

/**
 * One extracted rule. `bay_number_rule` and `shelf_rule` use the backend's
 * grammar: bays as "1 | 2", shelves as a number, "Top", "Top + N", "Bottom",
 * or "Bottom - N" (one shelf per rule). Empty string means the model didn't
 * pin that axis — the user must resolve it before saving, since the backend
 * resolves each rule to a concrete bay + shelf.
 */
export interface ParsedVariantRestrictionRule {
  variant: string;
  /** false when the model couldn't map the phrase to a known variant. */
  matched: boolean;
  confidence: number;
  suggestion?: string;
  bay_number_rule: string;
  shelf_rule: string;
  condition: VariantRestrictionCondition;
}

/**
 * A rule-based way to select stores, in addition to (or instead of) explicit
 * store codes. Resolved to concrete stores at save time against the category's
 * live store data. Each dimension is OR within its values; dimensions are ANDed
 * together (a store must match every non-empty dimension). All empty = no filter.
 */
export interface StoreFilter {
  /** Bay count (COUNT(DISTINCT section_num)). */
  bay_sizes: number[];
  states: string[];
  clusters: string[];
  /** Max section height across the store's fixtures. */
  section_heights: number[];
  /** Max section width across the store's fixtures. */
  section_widths: number[];
}

/**
 * A set of rules and the stores they apply to. The model may return several
 * groups when a description scopes rules to different stores. Store scope can be
 * explicit codes, a filter (e.g. "2 bay stores"), or both; empty on both means
 * all stores in the category.
 */
export interface ParsedVariantRestrictionGroup {
  store_codes: string[];
  /** store references the model couldn't resolve to a known store code. */
  unmatched_stores: string[];
  store_filter: StoreFilter;
  /**
   * When set to one of the provided `existing_group_names`, this group's rules
   * should be appended to that existing group rather than forming a new one; its
   * store scope is then ignored. null/absent means a new group.
   */
  target_group_name?: string | null;
  rules: ParsedVariantRestrictionRule[];
}

export interface ResponsePostMerchflowParseVariantRestrictions {
  groups: ParsedVariantRestrictionGroup[];
}
